import { useCallback, useEffect, useRef, useState } from "react";
import { createBarcodeScanner } from "../lib/liveBarcode";
import { openRearCamera, SCAN_QUALITY } from "../lib/cameraStream";

// A camera that reads ONE barcode and hands it back.
//
// Extracted rather than copied out of CaptureCamera: both surfaces now open a
// camera and decode a barcode, and two implementations of that would drift —
// one of them would get the track-death watchdog or the BarcodeDetector/ZXing
// split and the other would not. This is the small, single-purpose version;
// CaptureCamera keeps its own because it also has to hold the stream open for
// photographs afterwards.
//
// Opens on demand and releases the camera the moment it closes. A scanner left
// running behind a closed modal keeps the recording indicator lit, which is
// alarming and looks like a bug.

const CSS = `
.bs-wrap{
  position:fixed; inset:0; z-index:20; background:#07100E;
  display:flex; flex-direction:column; color:#E7EEEB;
  font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;
}
.bs-head{
  display:flex; align-items:center; gap:12px;
  padding:calc(env(safe-area-inset-top,0px) + var(--pad,18px)) var(--pad,18px) calc(var(--pad,18px) * .7);
}
.bs-title{
  flex:1 1 0; min-width:0; margin:0;
  font-size:clamp(19px,5vw,34px); font-weight:600; letter-spacing:-.015em;
}
.bs-close{
  flex:0 0 auto; width:clamp(46px,12vw,88px); height:clamp(46px,12vw,88px);
  border-radius:999px; border:0; padding:0; cursor:pointer;
  background:rgba(255,255,255,.14); color:#E7EEEB;
  font-size:clamp(22px,6vw,40px); line-height:1; display:grid; place-items:center;
}
.bs-stage{
  flex:1; min-height:0; position:relative; overflow:hidden; background:#000;
}
.bs-video{
  position:absolute; inset:0; width:100%; height:100%;
  object-fit:cover; display:block; background:#000;
}
.bs-reticle{
  position:absolute; left:50%; top:50%; transform:translate(-50%,-50%);
  width:min(82vw,420px); height:min(24vh,190px); border-radius:20px;
  border:3px solid rgba(255,255,255,.6);
  box-shadow:0 0 0 100vmax rgba(0,0,0,.3);
}
.bs-foot{
  padding:calc(var(--pad,18px) * .8) var(--pad,18px)
          calc(env(safe-area-inset-bottom,0px) + var(--pad,18px));
  text-align:center; color:#9DAAA5; font-size:clamp(14px,3.8vw,26px);
  line-height:1.4;
}
.bs-panel{
  position:absolute; inset:0; display:flex; flex-direction:column;
  align-items:center; justify-content:center; gap:14px; text-align:center;
  padding:32px 24px; background:#0b0f0e;
}
.bs-panel p{margin:0; color:#9DAAA5; font-size:clamp(15px,4vw,28px); max-width:34ch; line-height:1.5;}
.bs-btn{
  appearance:none; border:0; border-radius:999px; font:inherit; font-weight:600;
  font-size:clamp(16px,4.4vw,30px); padding:0 28px;
  min-height:clamp(50px,13vw,96px); background:#19C39B; color:#04211B;
  cursor:pointer;
}
.bs-wrap button:focus-visible{outline:2px solid #67E8C4; outline-offset:2px;}
`;

export default function BarcodeScanModal({ open, onResult, onClose }) {
  const [status, setStatus] = useState("starting"); // starting|scanning|denied|error
  const [engine, setEngine] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scannerRef = useRef(null);
  // The result must fire exactly once. The scanner stops itself on a hit, but
  // the close that follows is asynchronous and a second frame can still be in
  // flight — and a second result would overwrite the search the first one set.
  const doneRef = useRef(false);

  const release = useCallback(() => {
    scannerRef.current?.stop();
    scannerRef.current = null;
    const stream = streamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    doneRef.current = false;
    let cancelled = false;

    (async () => {
      setStatus("starting");
      try {
        // Was an unconstrained getUserMedia, which Chrome answers with
        // 640x480 — a soft, upscaled preview and fewer pixels per barcode
        // module to decode from. The constraints now live in one place shared
        // with the capture page.
        const stream = await openRearCamera(SCAN_QUALITY);
        // The modal may have been closed while the permission prompt was up.
        if (cancelled || !videoRef.current) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
        setStatus("scanning");

        scannerRef.current = createBarcodeScanner({
          video: videoRef.current,
          onResult: (value) => {
            if (doneRef.current) return;
            doneRef.current = true;
            try {
              navigator.vibrate?.([35, 45, 70]);
            } catch {
              /* best-effort */
            }
            onResult(value);
          },
        });
        await scannerRef.current.start();
        setEngine(scannerRef.current.engine);
      } catch (err) {
        if (cancelled) return;
        const name = err?.name || "";
        setStatus(
          name === "NotAllowedError" || name === "SecurityError"
            ? "denied"
            : "error",
        );
      }
    })();

    return () => {
      cancelled = true;
      release();
    };
  }, [open, onResult, release]);

  // Escape closes, matching the photo viewer on the same page.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="bs-wrap" role="dialog" aria-modal="true">
      <style dangerouslySetInnerHTML={{ __html: CSS }} />
      <div className="bs-head">
        <h2 className="bs-title">Scan to search</h2>
        <button type="button" className="bs-close" onClick={onClose} aria-label="Close scanner">
          &times;
        </button>
      </div>

      <div className="bs-stage">
        <video ref={videoRef} className="bs-video" playsInline muted autoPlay />
        {status === "scanning" ? <div className="bs-reticle" /> : null}

        {status === "denied" ? (
          <div className="bs-panel">
            <p>
              Camera access is blocked for this site. Allow it in your browser
              settings, or type the barcode instead.
            </p>
            <button type="button" className="bs-btn" onClick={onClose}>
              Type it instead
            </button>
          </div>
        ) : null}

        {status === "error" ? (
          <div className="bs-panel">
            <p>The camera could not be started. Type the barcode instead.</p>
            <button type="button" className="bs-btn" onClick={onClose}>
              Close
            </button>
          </div>
        ) : null}
      </div>

      <p className="bs-foot">
        {status === "scanning"
          ? `Point at a barcode${engine === "zxing" ? " — hold steady" : ""}`
          : status === "starting"
            ? "Starting the camera…"
            : ""}
      </p>
    </div>
  );
}
